import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Mic2, Clapperboard, Sparkles, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { MaskedLine, FadeIn, Eyebrow, Tag } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import { HERO_SCENES, KITTY, RADIO_LOGO, FOURTHWALL_URL, apiFetch } from "@/lib/brand";
import { TRACKS } from "@/lib/tracks";
import { RadioTracks } from "@/components/Player";

const PORTALS = [
    { img: KITTY.thinking, kicker: "Portal One — AI Skeptic", heading: "LET'S TALK ABOUT AI", copy: "Not convinced AI belongs in creativity? Tell us what concerns you and what you want the future of creative work to look like.", cta: "SHARE YOUR PERSPECTIVE", path: "skeptic" },
    { img: KITTY.question, kicker: "Portal Two — The Curious Creative", heading: "I'M STILL FIGURING IT OUT", copy: "Curious about AI, creative development, or building a brand? Explore the possibilities without pressure to choose a side.", cta: "EXPLORE YOUR OPTIONS", path: "curious" },
    { img: KITTY.pointing, kicker: "Portal Three — The Tool User", heading: "LET'S BUILD SOMETHING", copy: "Already using AI or other creative tools? Discover ways to turn your ideas into a recognizable identity and a practical plan.", cta: "START BUILDING", path: "builder" },
];

const EXPLORE = [
    { icon: Mic2, title: "Artist Development", audience: "For singers, rappers, musicians, producers, DJs, and independent recording artists.", points: ["Artist identity", "Branding & positioning", "Creative direction", "Music rollout planning", "Visual identity", "Content strategy"], cta: "Start as an Artist", to: "/discover?path=builder" },
    { icon: Clapperboard, title: "Creator Development", audience: "For streamers, UGC creators, actors, filmmakers, and digital personalities.", points: ["Content concepts", "Audience positioning", "Personal branding", "Creative consistency", "Video & social planning"], cta: "Start as a Creator", to: "/discover?path=builder" },
    { icon: Sparkles, title: "The Dreamer Program", audience: "For people who have talent or ideas but don't know where to begin.", points: ["Discover your creative lane", "Identify strengths & interests", "Personal brand direction", "An initial roadmap", "Confidence through action"], cta: "Find Your Starting Point", to: "/discover?path=unsure", note: "An entry point for direction — not a guarantee of success." },
    { icon: GraduationCap, title: "PAN School", audience: "An educational area for branding, creative tools, content strategy, and independent business development.", points: [], cta: "Enter PAN School", to: "/pan-school" },
];

function ParkTesterForm() {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [claimed, setClaimed] = useState(null);

    const loadSpots = async () => {
        try {
            const d = await apiFetch("/notify/spots");
            setClaimed(d.claimed);
        } catch {
            /* counter is decorative — form still works */
        }
    };

    useEffect(() => {
        loadSpots();
    }, []);

    const full = claimed !== null && claimed >= 35;
    const [joinedWaitlist, setJoinedWaitlist] = useState(false);

    const counter = (
        <div className="mt-4" data-testid="park-spots-counter">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt" data-testid="park-spots-text">
                {claimed === null ? "35 spots total" : full ? "All 35 spots claimed — waitlist open" : `${claimed} of 35 spots claimed`}
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden max-w-xs">
                <div
                    className="h-full bg-volt transition-all duration-700"
                    style={{ width: `${Math.min(100, ((claimed || 0) / 35) * 100)}%` }}
                />
            </div>
        </div>
    );

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) {
            toast.error("Enter your name and email.");
            return;
        }
        setSending(true);
        try {
            await apiFetch("/notify", {
                method: "POST",
                body: { name: name.trim(), email: email.trim(), interest: full ? "park-tester-waitlist" : "park-tester" },
            });
            setJoinedWaitlist(full);
            setDone(true);
            loadSpots();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div>
                {counter}
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-volt leading-relaxed" data-testid="park-tester-success">
                    {joinedWaitlist
                        ? "You're on the P.A.R.K. waitlist — first in line when new spots open."
                        : "You're on the tester list. Watch your inbox — details land before launch."}
                </p>
            </div>
        );
    }
    if (!open) {
        return (
            <div>
                {counter}
                <button
                    onClick={() => setOpen(true)}
                    data-testid="park-tester-open"
                    className="mt-4 rounded-full bg-volt px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(163,230,53,0.45)]"
                >
                    {full ? "Join the Waitlist" : "Become a Tester"}
                </button>
            </div>
        );
    }
    return (
        <div>
            {counter}
            <form onSubmit={submit} className="mt-4 space-y-3 w-full" data-testid="park-tester-form">
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                aria-label="Your name"
                data-testid="park-tester-name"
                className="w-full rounded-full bg-panel/80 border border-white/15 px-5 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt/50 transition-colors"
                required
            />
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourlane.com"
                aria-label="Email address"
                data-testid="park-tester-email"
                className="w-full rounded-full bg-panel/80 border border-white/15 px-5 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt/50 transition-colors"
                required
            />
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="park-tester-submit"
                    className="rounded-full bg-volt px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(163,230,53,0.45)] disabled:opacity-60"
                >
                    {sending ? "Joining..." : full ? "Join the Waitlist" : "Claim a Spot"}
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="park-tester-cancel"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 transition-colors"
                >
                    Not now
                </button>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                {full ? "Spots are full — you're joining the waitlist." : "Android only this round — 35 spots."}
            </p>
        </form>
        </div>
    );
}

function PilotWaitlistForm() {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Enter your email.");
            return;
        }
        setSending(true);
        try {
            await apiFetch("/notify", { method: "POST", body: { email: email.trim(), interest: "pilot-waitlist" } });
            setDone(true);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-volt leading-relaxed" data-testid="pilot-waitlist-success">
                You're on the pilot waitlist.
            </p>
        );
    }
    return (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="pilot-waitlist-form">
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourlane.com"
                aria-label="Email address"
                data-testid="pilot-waitlist-email"
                className="flex-1 rounded-full bg-obsidian/80 border border-white/15 px-5 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors"
                required
            />
            <button
                type="submit"
                disabled={sending}
                data-testid="pilot-waitlist-submit"
                className="rounded-full bg-purp px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(168,85,247,0.55)] disabled:opacity-60 shrink-0"
            >
                {sending ? "Joining..." : "Join the Waitlist"}
            </button>
        </form>
    );
}

function Newsletter() {
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !consent) {
            toast.error("Enter your email and check the consent box.");
            return;
        }
        setSending(true);
        try {
            await apiFetch("/newsletter", { method: "POST", body: { email: email.trim(), consent, source: "homepage" } });
            setDone(true);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="py-20 md:py-28 border-t border-white/10" data-testid="newsletter-section">
            <div className="max-w-3xl mx-auto px-5 md:px-8">
                <FadeIn>
                    <div className="rounded-3xl bg-panel/75 backdrop-blur-md border border-purp/35 card-glow p-8 md:p-12 text-center">
                        <Eyebrow className="justify-center mb-5">Newsletter</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Stay in <span className="italic text-purp">the loop.</span>
                        </h2>
                        <p className="mt-5 text-slate-400 text-base leading-relaxed max-w-xl mx-auto">
                            Get updates on creative development, new resources, PAN projects, and opportunities to
                            keep building.
                        </p>
                        {done ? (
                            <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-volt" data-testid="newsletter-success">
                                You're in the loop. Talk soon.
                            </p>
                        ) : (
                            <form onSubmit={submit} className="mt-8 max-w-md mx-auto" data-testid="newsletter-form">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@yourlane.com"
                                        aria-label="Email address"
                                        data-testid="newsletter-email-input"
                                        className="flex-1 rounded-full bg-obsidian/80 border border-white/15 px-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors"
                                        required
                                    />
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="newsletter-submit"
                                        className="rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)] disabled:opacity-60"
                                    >
                                        {sending ? "Joining..." : "Join the Network"}
                                    </button>
                                </div>
                                <label className="mt-4 flex items-start gap-3 text-left cursor-pointer" data-testid="newsletter-consent-label">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded border-white/20 bg-obsidian accent-[#A855F7]"
                                        data-testid="newsletter-consent-checkbox"
                                    />
                                    <span className="text-xs text-slate-500 leading-relaxed">
                                        Yes — email me PAN updates. No spam, no selling data, unsubscribe anytime.
                                    </span>
                                </label>
                            </form>
                        )}
                        <div
                            className="mt-10 rounded-2xl border-2 border-volt/60 bg-obsidian/70 p-5 md:p-6 flex flex-col sm:flex-row items-center gap-5 text-left"
                            data-testid="park-callout"
                        >
                            <img
                                src="/brand/park-app.webp"
                                alt="P.A.R.K. — Purp Alert Ready Kit"
                                loading="lazy"
                                className="w-full sm:w-44 rounded-xl border border-purp/40 shrink-0"
                                data-testid="park-logo"
                            />
                            <div>
                                <p className="font-display font-extrabold uppercase text-lg sm:text-xl leading-tight text-volt">
                                    MY NEW APP IN DEVELOPMENT
                                </p>
                                <p className="mt-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.14em] text-slate-200 leading-relaxed">
                                    I'm looking for <span className="text-purp-neon font-bold">35 testers with Android devices</span> to
                                    test P.A.R.K. — Purp Alert Ready Kit. Apple users stay tuned until December 12, 2026.
                                </p>
                                <ParkTesterForm />
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const mascotY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 260]);
    const [scene, setScene] = useState(0);
    const [joining, setJoining] = useState(false);

    const joinMembership = async () => {
        setJoining(true);
        try {
            const d = await apiFetch("/create-checkout-session", { method: "POST" });
            window.location.href = d.checkout_url;
        } catch (err) {
            toast.error(err.message || "Checkout is being configured — try again soon.");
            setJoining(false);
        }
    };

    useEffect(() => {
        const t = setInterval(() => setScene((i) => (i + 1) % HERO_SCENES.length), 4500);
        return () => clearInterval(t);
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-14 md:pt-28 md:pb-20">
                <div className="absolute inset-0 bg-grid-purp opacity-70" />
                <motion.div style={{ y: glowY }} className="absolute inset-0 bg-radial-purp" />
                <div className="absolute -right-40 top-1/3 w-[560px] h-[560px] rounded-full bg-purp-deep/25 blur-[140px]" />

                <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center w-full">
                    <div>
                        <FadeIn y={12}>
                            <Eyebrow className="mb-7">Community Support with Urgency &amp; Purpose</Eyebrow>
                        </FadeIn>
                        <h1 className="font-display font-extrabold uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl tracking-tight" data-testid="hero-headline">
                            <MaskedLine delay={0.15}>Your talent.</MaskedLine>
                            <MaskedLine delay={0.27}>Your identity.</MaskedLine>
                            <MaskedLine delay={0.42}>
                                <span className="italic text-purp text-glow">Your next move.</span>
                            </MaskedLine>
                        </h1>
                        <FadeIn delay={0.7} className="mt-8 max-w-xl">
                            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
                                PURP ALERT NETWORK is a creative development and acquisition network helping
                                independent artists and aspiring creatives discover their lane, build their brand,
                                and turn their potential into a plan.
                            </p>
                            <p className="mt-4 text-slate-500 text-sm md:text-base leading-relaxed">
                                You don&apos;t have to have it all figured out. You just have to be willing to start.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.85} className="mt-6 md:mt-9 flex flex-wrap items-center gap-4">
                            <button
                                onClick={() => document.getElementById("pilot-card")?.scrollIntoView({ behavior: "smooth" })}
                                data-testid="hero-join-waitlist-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)]"
                            >
                                Join the Waitlist <ArrowRight className="w-4 h-4" />
                            </button>
                            <a
                                href={FOURTHWALL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-shop-drop-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-purp/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-purp-neon transition-colors duration-300 hover:bg-purp/10"
                            >
                                Shop the Drop <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <Link
                                to="/discover"
                                data-testid="hero-find-lane-btn"
                                className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-purp-neon transition-colors"
                            >
                                Find Your Lane
                            </Link>
                        </FadeIn>

                        {/* Mobile character — compact rotation for small screens */}
                        <FadeIn delay={0.95} className="lg:hidden mt-10">
                            <div className="relative w-full max-w-[240px] aspect-square rounded-3xl border border-purp/30 card-glow overflow-hidden" data-testid="hero-mascot-mobile">
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={scene}
                                        src={HERO_SCENES[scene].src}
                                        alt={`PURP ALERT — ${HERO_SCENES[scene].title}`}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.6, ease: "easeInOut" }}
                                    />
                                </AnimatePresence>
                            </div>
                        </FadeIn>
                    </div>

                    <motion.div style={{ y: mascotY }} className="relative hidden lg:block" data-testid="hero-mascot">
                        <motion.div
                            animate={{ y: [0, -16, 0] }}
                            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                            className="relative"
                        >
                            <div className="absolute inset-8 rounded-full bg-purp/30 blur-[90px]" />
                            <motion.div
                                initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                transition={{ duration: 1.2, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="relative w-full max-w-md mx-auto aspect-square rounded-3xl border border-purp/30 card-glow overflow-hidden"
                            >
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={scene}
                                        src={HERO_SCENES[scene].src}
                                        alt={`PURP ALERT — ${HERO_SCENES[scene].title}`}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        initial={{ opacity: 0, scale: 1.04 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.7, ease: "easeInOut" }}
                                    />
                                </AnimatePresence>
                                <span
                                    className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-200 bg-obsidian/70 backdrop-blur px-3 py-1.5 rounded-full border border-white/10"
                                    data-testid="hero-scene-counter"
                                >
                                    {HERO_SCENES[scene].title} · {String(scene + 1).padStart(2, "0")}/{HERO_SCENES.length}
                                </span>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <Marquee items={["Create", "Build", "Grow", "Connect", "More than a talent agency", "Bigger than a record company", "Find Your Lane"]} />

            {/* ARTIST PILOT — status strip */}
            <section id="pilot-card" className="border-t border-white/10 py-10 md:py-12 scroll-mt-24" data-testid="pilot-strip">
                <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
                    <div className="shrink-0">
                        <Eyebrow className="mb-3">Artist Pilot</Eyebrow>
                        <p className="font-display font-extrabold uppercase text-xl md:text-2xl leading-tight" data-testid="pilot-status">
                            All slots are filled for 2026.
                        </p>
                    </div>
                    <div className="flex-1 max-w-xl">
                        <PilotWaitlistForm />
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                            Joining the waitlist is free and does not guarantee a slot.
                        </p>
                    </div>
                </div>
            </section>

            {/* PORTALS */}
            <section id="portals" className="relative py-20 md:py-28" data-testid="portals-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn className="text-center max-w-2xl mx-auto">
                        <Eyebrow className="justify-center mb-5">Choose Your Portal</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Where are you <span className="italic text-purp">starting from?</span>
                        </h2>
                        <p className="mt-5 text-slate-400 text-base leading-relaxed">
                            No wrong answers. PAN welcomes different perspectives — pick the one that sounds like you.
                        </p>
                    </FadeIn>
                    <div className="mt-14 grid md:grid-cols-3 gap-5">
                        {PORTALS.map((p, i) => (
                            <FadeIn key={p.path} delay={0.1 * i}>
                                <div
                                    className="h-full rounded-3xl bg-panel/75 backdrop-blur-md border border-white/10 p-8 text-center hover:border-purp/50 transition-colors duration-300 flex flex-col"
                                    data-testid={`portal-${p.path}`}
                                >
                                    <div className="w-24 h-24 mx-auto rounded-full border-2 border-purp/60 overflow-hidden shadow-[0_0_44px_rgba(168,85,247,0.35)]">
                                        <img src={p.img} alt={`PURP KITTY — ${p.kicker}`} loading="lazy" className="w-full h-full object-cover" />
                                    </div>
                                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{p.kicker}</p>
                                    <h3 className="mt-2 font-display font-extrabold uppercase text-2xl md:text-3xl leading-tight">{p.heading}</h3>
                                    <p className="mt-4 text-sm text-slate-400 leading-relaxed flex-1">{p.copy}</p>
                                    <Link
                                        to={`/discover?path=${p.path}`}
                                        data-testid={`portal-cta-${p.path}`}
                                        className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-purp/50 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-purp-neon transition-all duration-300 hover:bg-purp hover:text-obsidian"
                                    >
                                        {p.cta} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={0.3} className="text-center mt-8">
                        <Link to="/pan-school" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-purp-neon transition-colors" data-testid="portal-unsure-link">
                            I&apos;m not sure yet — take me to PAN School <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>

            {/* WHAT PURP ALERT NETWORK DOES */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="what-pan-does-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">What PURP ALERT NETWORK Does</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
                            Your brand, <span className="italic text-purp">turned into a business.</span>
                        </h2>
                        <div className="mt-6 max-w-3xl space-y-5 text-slate-400 text-base md:text-lg leading-relaxed">
                            <p>
                                Purp Alert Network is a creative development and acquisition network. The core of
                                what I do is creative direction — I take a creator&apos;s brand and turn it into a
                                business.
                            </p>
                            <p>
                                &ldquo;Development&rdquo; means we build creators up. &ldquo;Acquisition&rdquo; means
                                the Network also brings artists in — signed to build with us, not just serviced from
                                the outside. Our first artist will be introduced next year.
                            </p>
                        </div>
                    </FadeIn>
                    <div className="mt-12 grid md:grid-cols-3 gap-5">
                        <FadeIn delay={0.05}>
                            <div className="h-full rounded-3xl bg-panel/75 backdrop-blur-md border border-white/10 p-7" data-testid="lane-step-by-step">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purp-neon">Step by Step — I Show You</p>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                    I walk you through turning your brand into a real business, in the right order,
                                    so nothing gets skipped.
                                </p>
                            </div>
                        </FadeIn>
                        <FadeIn delay={0.1}>
                            <div className="h-full rounded-3xl bg-panel/75 backdrop-blur-md border border-white/10 p-7" data-testid="lane-done-for-you">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purp-neon">Done for You — Premium</p>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                    I handle it for you. You bring the talent, I build the business around it.
                                </p>
                            </div>
                        </FadeIn>
                        <FadeIn delay={0.15}>
                            <div className="h-full rounded-3xl bg-panel/75 backdrop-blur-md border border-white/10 p-7" data-testid="lane-pan-school">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purp-neon">PAN School — Waitlist</p>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                    Join the waitlist for PAN School, opening after the Network is built. It teaches
                                    the exact process I used to build this.
                                </p>
                                <Link
                                    to="/pan-school"
                                    data-testid="lane-pan-school-link"
                                    className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-purp-neon hover:text-purp transition-colors"
                                >
                                    Join the PAN School waitlist <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        </FadeIn>
                    </div>
                    <FadeIn delay={0.2}>
                        <div className="mt-10 rounded-3xl border border-purp/30 bg-panel/60 p-8 max-w-3xl" data-testid="the-proof">
                            <Eyebrow className="mb-4">The Proof</Eyebrow>
                            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                                I&apos;m not teaching theory. I&apos;m the first example. I built my own brand into a
                                business — in public, step by step — and Purp Alert Network is the result.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* PURP ALERT RADIO */}
            <section id="radio" className="relative py-20 md:py-28 border-t border-white/10 scroll-mt-20" data-testid="radio-section">
                <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-[0.7fr_1fr] gap-10 items-center">
                    <FadeIn>
                        <div className="relative max-w-[280px] mx-auto">
                            <div className="absolute inset-8 rounded-full bg-purp/25 blur-[80px]" />
                            <img
                                src={RADIO_LOGO}
                                alt="PURP ALERT NETWORK RADIO — official logo"
                                loading="lazy"
                                className="relative w-full rounded-3xl border border-purp/30 card-glow"
                                data-testid="radio-logo"
                            />
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <Eyebrow className="mb-5">{TRACKS.length > 0 ? "The Playlist" : "Coming Soon"}</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            PURP ALERT <span className="italic text-purp text-glow">Radio.</span>
                        </h2>
                        <p className="mt-5 text-slate-400 text-base leading-relaxed max-w-lg">
                            The sound of the Network — music, MY 5 audio drops, and community frequencies.
                        </p>
                        <div className="mt-6"><Tag tone="volt">{TRACKS.length > 0 ? "Now Playing" : "In Development"}</Tag></div>
                        <RadioTracks />
                    </FadeIn>
                </div>
            </section>

            {/* EXPLORE THE NETWORK */}
            <section id="explore" className="relative py-20 md:py-28 border-t border-white/10 scroll-mt-20" data-testid="explore-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn className="max-w-2xl">
                        <Eyebrow className="mb-5">Explore the Network</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Four ways <span className="italic text-purp">in.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 gap-5">
                        {EXPLORE.map((x, i) => (
                            <FadeIn key={x.title} delay={0.08 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-8 hover:border-purp/50 transition-colors duration-300 flex flex-col" data-testid={`explore-card-${i}`}>
                                    <x.icon className="w-7 h-7 text-purp-neon" />
                                    <h3 className="mt-5 font-display font-extrabold uppercase text-3xl">{x.title}</h3>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{x.audience}</p>
                                    {x.points.length > 0 && (
                                        <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 flex-1">
                                            {x.points.map((pt) => (
                                                <li key={pt} className="flex items-start gap-2 text-xs text-slate-400">
                                                    <Check className="w-3.5 h-3.5 text-volt shrink-0 mt-0.5" /> {pt}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {x.note && <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{x.note}</p>}
                                    <Link
                                        to={x.to}
                                        data-testid={`explore-cta-${i}`}
                                        className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-purp/50 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-purp-neon transition-all duration-300 hover:bg-purp hover:text-obsidian"
                                    >
                                        {x.cta} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* MEDIA — button until material lands */}
            <section className="relative py-16 md:py-20 border-t border-white/10" data-testid="media-spotlight">
                <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <Link
                            to="/media"
                            data-testid="media-button"
                            className="inline-flex items-center gap-3 rounded-full border border-purp/50 px-10 py-5 font-display font-extrabold uppercase text-2xl sm:text-3xl text-slate-100 hover:text-purp-neon hover:border-purp-neon transition-colors duration-300"
                        >
                            PURP ALERT Media <ArrowRight className="w-6 h-6" />
                        </Link>
                    </FadeIn>
                </div>
            </section>

            {/* SHOP THE DROP */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="shop-drop-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <FadeIn>
                        <div className="relative rounded-3xl overflow-hidden border border-purp/30 card-glow aspect-[4/5] max-w-md mx-auto">
                            <img
                                src="/brand/character-portrait.webp"
                                alt="The #5 Jersey — PURP ALERT, First Drop"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.15}>
                        <Eyebrow className="mb-5">Official Store</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Shop the <span className="italic text-purp">Drop.</span>
                        </h2>
                        <p className="mt-5 text-slate-400 text-base md:text-lg leading-relaxed max-w-lg">
                            The official Purp Alert Network store — the #5 jersey, hoodies, tees, and more.
                        </p>
                        <p className="mt-6 font-display font-extrabold uppercase text-xl md:text-2xl text-slate-100">
                            First up: the #5 jersey.
                        </p>
                        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-purp-neon">
                            The 5 represents my 5th chance at LIFE.
                        </p>
                        <a
                            href={FOURTHWALL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="shop-drop-cta"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-purp px-10 py-4 font-mono text-sm uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)]"
                        >
                            Shop the Drop <ArrowUpRight className="w-5 h-5" />
                        </a>
                        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                            Prices, checkout, shipping, and returns all live on the Fourthwall store.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* MEMBERSHIP */}
            <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="membership-section">
                <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <Eyebrow className="justify-center mb-5">Membership</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Your next move <span className="italic text-purp">starts here.</span>
                        </h2>
                        <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                            Get connected to creative resources, opportunities to explore your direction, and a
                            network built around helping independent creatives move with purpose.
                        </p>
                        <div className="mt-10 max-w-md mx-auto rounded-3xl bg-panel/75 backdrop-blur-md border border-purp/35 card-glow p-8" data-testid="membership-card">
                            <h3 className="font-display font-extrabold uppercase text-2xl">PURP ALERT NETWORK Membership</h3>
                            <p className="mt-1 font-display font-extrabold text-5xl text-purp-neon" data-testid="membership-price">$4.99 USD<span className="text-lg text-slate-400">/month</span></p>
                            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recurring monthly subscription</p>
                            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                                Full details and renewal terms will be shown clearly before anything ever charges.
                            </p>
                            <button
                                type="button"
                                onClick={joinMembership}
                                disabled={joining}
                                data-testid="membership-cta"
                                className="mt-7 inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)] disabled:opacity-60"
                            >
                                {joining ? "Opening Checkout..." : "Join Membership"} <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </FadeIn>
                </div>
            </section>

            <Newsletter />

            {/* CTA BAND */}
            <section className="relative py-24 md:py-36 border-t border-white/10 overflow-hidden" data-testid="home-cta-band">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-purp-deep/20 blur-[160px]" />
                <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <h2 className="font-display font-extrabold uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.92]">
                            You don&apos;t need permission.<br />
                            <span className="italic text-purp text-glow">You need a lane.</span>
                        </h2>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <Link
                                to="/discover"
                                data-testid="home-cta-find-lane-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-purp px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_48px_rgba(168,85,247,0.7)]"
                            >
                                Find Your Lane <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/get-involved"
                                data-testid="home-cta-get-involved-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-purp/50 px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-purp-neon transition-colors duration-300 hover:bg-purp/10"
                            >
                                Partner With Us
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
